<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container attendance-report-page">
    <oxd-table-filter :filter-title="$t('time.attendance_report')">
      <oxd-form @submit-valid="generateCalendar" @reset="generateCalendar">
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.fromDate"
                type="date"
                :rules="rules.valueX"
                :label="$t('general.from_date')"
                :show-empty-selector="false"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.toDate"
                type="date"
                :rules="rules.valueY"
                :label="$t('general.to_date')"
                :show-empty-selector="false"
              />
            </oxd-grid-item>
            <oxd-grid-item class="--span-column-2">
              <oxd-input-field
                v-model="selectedShifts"
                type="multiselect"
                :label="$t('admin.work_shifts')"
                :options="shiftOptions"
                :show-empty-selector="false"
              />
            </oxd-grid-item>
            <!--            <oxd-grid-item>-->
            <!--              <oxd-input-field-->
            <!--                v-model="selectedSubUnit"-->
            <!--                type="select"-->
            <!--                :label="$t('general.sub_unit')"-->
            <!--                :options="filteredSubunits"-->
            <!--                :show-empty-selector="false"-->
            <!--              />-->
            <!--            </oxd-grid-item>-->
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="showWorkShift"
                type="checkbox"
                :option-label="$t('time.show_workshift')"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="showUnassigned"
                type="checkbox"
                :option-label="$t('time.show_unassigned')"
                @change="toggleUnassigned"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <oxd-button
            class="orangehrm-left-space"
            display-type="secondary"
            :label="$t('general.search')"
            type="submit"
          />
        </oxd-form-actions>
      </oxd-form>
    </oxd-table-filter>

    <div class="orangehrm-paper-container attendance-report-results">
      <div
        ref="fullscreenContainer"
        :class="[
          'fullscreen-container',
          'attendance-report-fullbleed',
          {active: false},
        ]"
      >
        <div class="orangehrm-container attendance-report-scroll-host">
          <div v-if="isLoading" class="loader-wrapper">
            <oxd-table-loader>
              <div class="oxd-loading-spinner-container" data-v-a6dfc6a9="">
                <div class="oxd-loading-spinner" data-v-a6dfc6a9=""></div>
              </div>
            </oxd-table-loader>
          </div>
          <div v-if="!isLoading" class="calendar-wrapper">
            <div class="calendar-container">
              <div
                class="calendar-inner"
                :class="{'calendar-inner--with-shift': showWorkShift}"
              >
                <div class="sidebar">
                  <div class="header-cell empty">
                    <oxd-icon
                      class="orangehrm-file-icon"
                      name="arrows-fullscreen"
                      @click="toggleFullscreen"
                    />
                    {{ $t('time.attendance_report_employee') }}
                  </div>
                  <template
                    v-for="dept in employeesByDepartment"
                    :key="dept.department"
                  >
                    <div class="department-row">{{ dept.department }}</div>
                    <div
                      v-for="employee in dept.employees"
                      :key="employee.id"
                      class="employee-cell"
                    >
                      <div class="employee-name-row">
                        <span class="employee-name">{{ employee.name }}</span>
                        <button
                          type="button"
                          class="employee-details"
                          @click.stop="openPeriodDetails(employee)"
                        >
                          (details)
                        </button>
                      </div>
                      <!--                  <span class="employee-job">{{ employee.jobTitle }}</span>-->
                      <span v-if="showWorkShift" class="employee-job"
                        >({{ employee.workShift.startTime }} -
                        {{ employee.workShift.endTime }})</span
                      >
                    </div>
                  </template>
                </div>

                <!-- Main Calendar Cells -->
                <div class="calendar-scroll">
                  <!-- Date and Day headers -->
                  <div class="calendar-row date-row">
                    <div
                      v-for="date in calendarDates"
                      :key="date.full"
                      class="header-cell date cell"
                    >
                      {{ formatDate(date.full) }}
                    </div>
                  </div>

                  <div class="calendar-row attendance-row">
                    <div
                      v-for="date in calendarDates"
                      :key="date.full + '-day'"
                      class="header-cell day cell"
                    >
                      {{ getFullDayName(date.full) }}
                    </div>
                  </div>

                  <!-- Calendar rows by department -->
                  <template
                    v-for="dept in employeesByDepartment"
                    :key="dept.department"
                  >
                    <div class="calendar-row department-label">
                      <div
                        class="department-cell"
                        :colspan="calendarDates.length"
                      >
                        &nbsp;
                      </div>
                    </div>
                    <div
                      v-for="employee in dept.employees"
                      :key="employee.id"
                      class="calendar-row"
                    >
                      <div
                        v-for="date in calendarDates"
                        :key="employee.id + '-' + date.full"
                        class="attendance-cell cell"
                        :class="
                          getAttendanceClass(employee.attendance?.[date.full])
                        "
                        :style="
                          getAttendanceCellStyle(
                            employee.attendance?.[date.full],
                          )
                        "
                        @click="openAttendanceCellDetail(employee, date.full)"
                      >
                        <div class="attendance-content">
                          <div
                            v-for="(att, i) in employee.attendance?.[
                              date.full
                            ] || []"
                            :key="i"
                            :title="att.tooltip || ''"
                          >
                            {{ att.text }}
                            <!-- <template v-if="att.hours !== null">
                            {{ att.hours.toFixed(2) }}
                          </template> -->
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>

                <div class="sidebar-payroll-totals">
                  <div class="header-cell empty payroll-total-header">
                    <span
                      v-for="column in payrollTotalColumns"
                      :key="column.key"
                      class="payroll-total-col"
                      :title="column.title"
                    >
                      {{ column.label }}
                    </span>
                  </div>
                  <template
                    v-for="dept in employeesByDepartment"
                    :key="dept.department"
                  >
                    <div class="department-row"></div>
                    <div
                      v-for="employee in dept.employees"
                      :key="employee.id"
                      class="employee-cell payroll-total-values"
                    >
                      <span
                        v-for="column in payrollTotalColumns"
                        :key="column.key"
                        class="payroll-total-col"
                        :title="column.title"
                      >
                        {{
                          formatPayrollTotal(
                            column.key,
                            employee.payrollTotals?.[column.key],
                          )
                        }}
                      </span>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!isLoading" class="attendance-report-export">
        <oxd-button
          display-type="success"
          :label="$t('general.export_to_excel')"
          type="button"
          @click="exportToExcel"
        />
      </div>
    </div>

    <div
      v-if="canViewAdminActionLog && !isLoading"
      class="orangehrm-paper-container attendance-admin-action-log"
    >
      <div class="orangehrm-header-container">
        <oxd-text tag="h6" class="orangehrm-main-title">
          {{ $t('time.admin_action_history') }}
        </oxd-text>
      </div>
      <oxd-form class="attendance-admin-action-log-filters">
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="adminActionLogFilters.category"
                type="select"
                :label="$t('general.type')"
                :options="adminActionLogCategoryOptions"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="adminActionLogFilters.empNumber"
                type="select"
                :label="$t('general.employee')"
                :options="adminActionLogEmployeeOptions"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="adminActionLogFilters.performedByUserId"
                type="select"
                :label="$t('general.performed_by')"
                :options="adminActionLogPerformedByOptions"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
      </oxd-form>
      <div class="orangehrm-container">
        <oxd-card-table
          v-if="filteredAdminActionLogRows.length > 0"
          :headers="adminActionLogHeaders"
          :items="filteredAdminActionLogRows"
          :selectable="false"
          :clickable="false"
          row-decorator="oxd-table-decorator-card"
        />
        <oxd-text v-else tag="p" class="orangehrm-empty-state">
          {{
            adminActionLogRows.length > 0
              ? $t('general.no_records_found')
              : $t('time.admin_action_no_records')
          }}
        </oxd-text>
      </div>
    </div>

    <attendance-period-details-dialog
      v-if="periodDetails.show"
      :employee-name="periodDetails.employeeName"
      :from-date="reportDateParam(filters.fromDate)"
      :to-date="reportDateParam(filters.toDate)"
      :entries="periodDetails.entries"
      @close="closePeriodDetails"
    />

    <attendance-cell-detail-modal
      v-if="attendanceDetailModal.show"
      :employee-name="attendanceDetailModal.employeeName"
      :emp-number="attendanceDetailModal.empNumber"
      :day-entry="attendanceDetailModal.dayEntry"
      :report-start-date="filters.fromDate"
      :report-end-date="filters.toDate"
      :can-edit-attendance="canEditAttendance"
      :can-delete-attendance="canDeleteAttendance"
      :can-edit-leave="canEditLeave"
      :can-edit-shift-permission="canEditShiftPermission"
      :auth-employee-number="authEmployeeNumber"
      @close="closeAttendanceCellDetail"
      @saved="onAttendanceDetailSaved"
    />
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import AttendanceCellDetailModal from '@/orangehrmTimePlugin/components/AttendanceCellDetailModal.vue';
import AttendancePeriodDetailsDialog from '@/orangehrmTimePlugin/components/AttendancePeriodDetailsDialog.vue';
import ExcelJS from 'exceljs';
import {saveAs} from 'file-saver';
import moment from 'moment';
import {
  endDateShouldBeAfterStartDate,
  required,
  startDateShouldBeBeforeEndDate,
  validDateFormat,
} from '@/core/util/validation/rules';
import usei18n from '@/core/util/composable/usei18n';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {computed, ref} from 'vue';
import {OxdIcon} from '@ohrm/oxd';
import {formatDate, formatTime, parseDate} from '@/core/util/helper/datefns';
import useLocale from '@/core/util/composable/useLocale';
import useEmployeeNameTranslate from '@/core/util/composable/useEmployeeNameTranslate';

const defaultFilters = {
  fromDate: null,
  toDate: null,
};

const http = new APIService(
  window.appGlobal.baseUrl,
  '/api/v2/attendance/reports/monthly',
);

const adminActionLogHttp = new APIService(
  window.appGlobal.baseUrl,
  '/api/v2/attendance/reports/admin-action-logs',
);

/** Default fills for attendance grid / Excel (ARGB). Custom cells use leave type background + optional foreground. */
const EXPORT_ATTENDANCE_COLOR_MAP = {
  'off-day': {bg: 'FFDEECFF', color: 'FF1E3A5F'},
  'absence-day': {bg: 'FFFFE5E5', color: 'FF9F1239'},
  'leave-holiday': {bg: 'FFDDF0E2', color: 'FF14532D'},
  'leave-sl': {bg: 'FFFDE6E6', color: 'FF991B1B'},
  'leave-al': {bg: 'FFDBF1D9', color: 'FF14532D'},
};

const PAYROLL_TOTAL_COLUMNS = [
  {key: 'attendance', label: 'ATT', title: 'Attendance days'},
  {key: 'absence', label: 'ABS', title: 'Absence days'},
  {key: 'annual', label: 'AL', title: 'Annual leave'},
  {key: 'sick', label: 'SL', title: 'Sick leave'},
  {key: 'permission', label: 'PERM', title: 'Permission leave'},
  {key: 'holiday', label: 'PH', title: 'Public holidays'},
  {key: 'lateMinutes', label: 'LATE', title: 'Late minutes'},
  {key: 'earlyMinutes', label: 'EARLY', title: 'Early minutes'},
];

/**
 * @param {string|null|undefined} value
 * @returns {string|null} #RRGGBB or null
 */
function normalizeAttendanceHexColor(value) {
  if (value == null || String(value).trim() === '') {
    return null;
  }
  let h = String(value).trim();
  if (h[0] !== '#') {
    h = `#${h}`;
  }
  if (!/^#[0-9A-Fa-f]{6}$/.test(h)) {
    return null;
  }
  return h.toUpperCase();
}

function relativeLuminanceFromRgb(rgb) {
  const lin = (c) => {
    const x = c / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  };
  const r = lin(rgb.r);
  const g = lin(rgb.g);
  const b = lin(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * @param {string} hex #RRGGBB
 * @returns {{r: number, g: number, b: number}|null}
 */
function rgbFromHex(hex) {
  const n = normalizeAttendanceHexColor(hex);
  if (!n) {
    return null;
  }
  const s = n.slice(1);
  return {
    r: parseInt(s.slice(0, 2), 16),
    g: parseInt(s.slice(2, 4), 16),
    b: parseInt(s.slice(4, 6), 16),
  };
}

function foregroundHexForAttendanceBg(hex) {
  const rgb = rgbFromHex(hex);
  if (!rgb) {
    return '#374151';
  }
  return relativeLuminanceFromRgb(rgb) > 0.55 ? '#14532d' : '#f9fafb';
}

/**
 * @param {{color?: string|null, foregroundColor?: string|null}} detail
 * @returns {string} #RRGGBB for text
 */
function resolvedAttendanceForegroundHex(detail) {
  const bg = normalizeAttendanceHexColor(detail?.color);
  if (!bg) {
    return '#374151';
  }
  const fg = normalizeAttendanceHexColor(detail?.foregroundColor);
  if (fg) {
    return fg;
  }
  return foregroundHexForAttendanceBg(bg);
}

/**
 * @param {string} hex #RRGGBB
 * @returns {string|null} AARRGGBB for ExcelJS
 */
function webHexToExcelArgb(hex) {
  const n = normalizeAttendanceHexColor(hex);
  if (!n) {
    return null;
  }
  return `FF${n.slice(1).toUpperCase()}`;
}

function isWorkAttendanceEntry(entry) {
  return entry?.isWork === true;
}

function leaveAbbreviation(entry) {
  const leaveName = entry.leave?.name || '';
  let text = (entry.leave?.abbreviation || '').trim();
  if (text) {
    return text;
  }
  const fromSlash = [];
  leaveName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .forEach((word) => {
      word.split('/').forEach((segment) => {
        const piece = segment.trim();
        if (piece.length) {
          fromSlash.push(piece.charAt(0).toUpperCase());
        }
      });
    });
  return fromSlash.join('').slice(0, 10) || 'X';
}

function payrollCellTooltip(entry) {
  const lines = [];
  const punchList = Array.isArray(entry.attendances) ? entry.attendances : [];
  const hourLines = punchList
    .map((att) => Number(att.workHours) || 0)
    .filter((hours) => hours > 0)
    .map((hours) => `${hours.toFixed(2)} hours`);
  if (hourLines.length) {
    lines.push(hourLines.join('\n'));
  }
  if (entry.leave?.name) {
    lines.push(entry.leave.name);
  }
  if (entry.publicHoliday?.name) {
    lines.push(entry.publicHoliday.name);
  }
  const late = Number(entry.payroll?.lateMinutes) || 0;
  const early = Number(entry.payroll?.earlyMinutes) || 0;
  if (late > 0) {
    lines.push(`Late ${late} min`);
  }
  if (early > 0) {
    lines.push(`Early ${early} min`);
  }
  return lines.join('\n');
}

function formatPayrollDayFraction(value) {
  const rounded = Math.round((Number(value) || 0) * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

function formatPayrollTotalValue(key, value) {
  const amount = Number(value) || 0;
  if (key === 'lateMinutes' || key === 'earlyMinutes') {
    return String(Math.round(amount));
  }
  return (Math.round(amount * 100) / 100).toFixed(2);
}

function sumPayrollTotals(calendar) {
  const today = moment().format('YYYY-MM-DD');
  const totals = {
    attendance: 0,
    absence: 0,
    annual: 0,
    sick: 0,
    permission: 0,
    holiday: 0,
    lateMinutes: 0,
    earlyMinutes: 0,
  };
  (calendar || []).forEach((entry) => {
    if (!entry?.date || entry.date > today || entry.payroll?.skipped) {
      return;
    }
    const payroll = entry.payroll || {};
    totals.attendance += Number(payroll.attendance) || 0;
    totals.absence += Number(payroll.absence) || 0;
    totals.annual += Number(payroll.annual) || 0;
    totals.sick += Number(payroll.sick) || 0;
    totals.permission += Number(payroll.permission) || 0;
    totals.holiday += Number(payroll.holiday) || 0;
    totals.lateMinutes += Number(payroll.lateMinutes) || 0;
    totals.earlyMinutes += Number(payroll.earlyMinutes) || 0;
  });
  return totals;
}

function punchHoursOnDay(entry) {
  const punches = Array.isArray(entry.attendances) ? entry.attendances : [];
  const hours =
    entry.workHours != null
      ? Number(entry.workHours) || 0
      : punches.reduce((sum, att) => sum + (Number(att.workHours) || 0), 0);
  return {punches, hours};
}

function formatPunchHours(hours) {
  return (Math.round(hours * 100) / 100).toFixed(2);
}

function scheduledOffCell(entry, tooltip) {
  const {punches, hours} = punchHoursOnDay(entry);
  if (punches.length === 0 && hours <= 0) {
    return [{text: 'OFF', isOff: true, hours: null, tooltip}];
  }
  return [
    {
      text: formatPunchHours(hours),
      isOff: true,
      hours: null,
      tooltip,
    },
  ];
}

function leaveCell(entry, tooltip) {
  const mark = leaveAbbreviation(entry);
  const {punches, hours} = punchHoursOnDay(entry);
  const hasAttendance = punches.length > 0 || hours > 0;
  return [
    {
      text: hasAttendance ? formatPunchHours(hours) : mark,
      leaveMark: mark,
      hours: null,
      tooltip,
      color: normalizeAttendanceHexColor(entry.leave?.color),
      foregroundColor: normalizeAttendanceHexColor(
        entry.leave?.foregroundColor,
      ),
    },
  ];
}

function payrollDayCells(entry) {
  const payroll = entry.payroll || {};
  const isFuture = entry.date > moment().format('YYYY-MM-DD');
  const paidLeave =
    (Number(payroll.annual) || 0) +
    (Number(payroll.sick) || 0) +
    (Number(payroll.permission) || 0);
  const holiday = Number(payroll.holiday) || 0;
  const attendance = Number(payroll.attendance) || 0;
  const absence = Number(payroll.absence) || 0;
  const nonWorking =
    payroll.nonWorking === true || entry.isWorkWeekDay === false;
  const tooltip = payrollCellTooltip(entry);
  const holidayDetail = {
    text: 'PH',
    hours: null,
    tooltip: tooltip || entry.publicHoliday?.name || 'PH',
  };
  const dayPunches = punchHoursOnDay(entry);
  const leaveWithAttendance =
    Boolean(entry.leave?.name) &&
    (dayPunches.punches.length > 0 || dayPunches.hours > 0);

  if (leaveWithAttendance) {
    return leaveCell(entry, tooltip);
  }

  if (isFuture) {
    if (entry.leave?.name) {
      return leaveCell(entry, tooltip);
    }
    if (holiday > 0) {
      return [holidayDetail];
    }
    if (nonWorking) {
      return scheduledOffCell(entry, tooltip);
    }
    return [];
  }

  if (holiday > 0) {
    return [holidayDetail];
  }
  if (paidLeave > 0 && entry.leave?.name) {
    return leaveCell(entry, tooltip);
  }
  if (nonWorking) {
    return scheduledOffCell(entry, tooltip);
  }
  if (attendance > 0) {
    return [
      {
        text: formatPayrollDayFraction(attendance),
        isWork: true,
        hours: null,
        tooltip,
      },
    ];
  }
  if (absence > 0) {
    return [{text: 'ABS', hours: null, tooltip, isAbsence: true}];
  }
  return [];
}

export default {
  name: 'MonthlyAttendance',
  components: {
    'oxd-icon': OxdIcon,
    'attendance-cell-detail-modal': AttendanceCellDetailModal,
    'attendance-period-details-dialog': AttendancePeriodDetailsDialog,
  },

  props: {
    subunits: {
      type: Array,
      default: () => [],
    },
    canEditAttendance: {
      type: Boolean,
      default: false,
    },
    canDeleteAttendance: {
      type: Boolean,
      default: false,
    },
    canEditLeave: {
      type: Boolean,
      default: false,
    },
    canEditShiftPermission: {
      type: Boolean,
      default: false,
    },
    canViewAdminActionLog: {
      type: Boolean,
      default: false,
    },
    authEmployeeNumber: {
      type: Number,
      default: null,
    },
    defaultFromDate: {
      type: String,
      default: null,
    },
    defaultToDate: {
      type: String,
      default: null,
    },
    workShifts: {
      type: Array,
      default: () => [],
    },
  },

  setup() {
    const filters = ref({
      ...defaultFilters,
    });
    const {$t} = usei18n();
    const {userDateFormat, jsDateFormat} = useDateFormat();
    const {locale} = useLocale();
    const {$tEmpName} = useEmployeeNameTranslate();
    const rules = {
      operator: [required],
      valueX: [
        required,
        validDateFormat(userDateFormat),
        startDateShouldBeBeforeEndDate(
          () => filters.value.toDate,
          $t('general.from_date_should_be_before_to_date'),
          {allowSameDate: true},
        ),
      ],
      valueY: [
        required,
        validDateFormat(userDateFormat),
        endDateShouldBeAfterStartDate(
          () => filters.value.fromDate,
          $t('general.to_date_should_be_after_from_date'),
          {allowSameDate: true},
        ),
      ],
    };

    const serializedFilters = computed(() => {
      return {
        fromDate: filters.value.fromDate,
        toDate: filters.value.toDate,
      };
    });

    return {
      rules,
      filters,
      serializedFilters,
      userDateFormat,
      jsDateFormat,
      locale,
      tEmpName: $tEmpName,
    };
  },
  data() {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 3;
    const months = [
      {id: 0, value: 0, label: 'January'},
      {id: 1, value: 1, label: 'February'},
      {id: 2, value: 2, label: 'March'},
      {id: 3, value: 3, label: 'April'},
      {id: 4, value: 4, label: 'May'},
      {id: 5, value: 5, label: 'June'},
      {id: 6, value: 6, label: 'July'},
      {id: 7, value: 7, label: 'August'},
      {id: 8, value: 8, label: 'September'},
      {id: 9, value: 9, label: 'October'},
      {id: 10, value: 10, label: 'November'},
      {id: 11, value: 11, label: 'December'},
    ];
    const selectedSubUnit = null;
    return {
      months,
      selectedSubUnit,
      yearRange: Array.from({length: 4}, (_, i) => {
        const y = startYear + i;
        return {id: i, value: y, label: String(y)};
      }),
      selectedYear: null,
      selectedMonth: null,
      isLoading: true,
      rawEmployees: [],
      employeesByDepartment: [],
      employeesByDepartmentUnfiltered: [],
      employees: [],
      calendarDates: [],
      editingCell: {empId: null, date: null},
      filteredSubunits: [],
      showWorkShift: false,
      shiftOptions: [],
      selectedShifts: [],
      showUnassigned: true,
      reportShiftLabel: '',
      payrollTotalColumns: PAYROLL_TOTAL_COLUMNS,
      isFullscreen: false,
      attendanceDetailModal: {
        show: false,
        employeeName: '',
        dayEntry: null,
      },
      periodDetails: {
        show: false,
        employeeName: '',
        entries: [],
      },
      adminActionLogRows: [],
      adminActionLogFilters: {
        empNumber: null,
        category: null,
        performedByUserId: null,
      },
    };
  },

  computed: {
    adminActionLogEmployeeOptions() {
      const byEmpNumber = new Map();
      this.adminActionLogRows.forEach((row) => {
        if (row.empNumber != null && !byEmpNumber.has(row.empNumber)) {
          byEmpNumber.set(row.empNumber, {
            id: row.empNumber,
            label: row.employee,
          });
        }
      });

      return Array.from(byEmpNumber.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      );
    },
    adminActionLogCategoryOptions() {
      return [
        {
          id: 'attendance',
          label: this.$t('time.admin_action_category_attendance'),
        },
        {
          id: 'leave',
          label: this.$t('time.admin_action_category_leave'),
        },
      ];
    },
    adminActionLogPerformedByOptions() {
      const byId = new Map();
      this.adminActionLogRows.forEach((row) => {
        if (row.performedByUserId != null && !byId.has(row.performedByUserId)) {
          byId.set(row.performedByUserId, {
            id: row.performedByUserId,
            label: row.performedBy,
          });
        }
      });

      return Array.from(byId.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      );
    },
    filteredAdminActionLogRows() {
      const empNumber = this.resolveAdminActionLogFilterId(
        this.adminActionLogFilters.empNumber,
      );
      const category = this.resolveAdminActionLogFilterId(
        this.adminActionLogFilters.category,
      );
      const performedByUserId = this.resolveAdminActionLogFilterId(
        this.adminActionLogFilters.performedByUserId,
      );

      return this.adminActionLogRows.filter((row) => {
        if (empNumber != null && row.empNumber != empNumber) {
          return false;
        }
        if (category != null && row.categoryKey != category) {
          return false;
        }
        if (
          performedByUserId != null &&
          row.performedByUserId != performedByUserId
        ) {
          return false;
        }

        return true;
      });
    },
    adminActionLogHeaders() {
      return [
        {
          name: 'performedAt',
          slot: 'title',
          title: this.$t('time.admin_action_performed_at'),
          style: {flex: 1.2},
        },
        {
          name: 'category',
          title: this.$t('general.type'),
          style: {flex: 0.8},
        },
        {
          name: 'action',
          title: this.$t('general.actions'),
          style: {flex: 0.8},
        },
        {
          name: 'employee',
          title: this.$t('general.employee'),
          style: {flex: 1.2},
        },
        {
          name: 'targetDate',
          title: this.$t('time.admin_action_target_date'),
          style: {flex: 1},
        },
        {
          name: 'performedBy',
          title: this.$t('general.performed_by'),
          style: {flex: 1},
        },
        {
          name: 'summary',
          title: this.$t('time.admin_action_summary'),
          style: {flex: 2},
        },
      ];
    },
    currentYear() {
      return new Date().getFullYear();
    },
    currentMonth() {
      return new Date().getMonth();
    },
  },

  mounted() {
    // Listen for fullscreen change to keep `isFullscreen` in sync
    document.addEventListener('fullscreenchange', () => {
      this.isFullscreen = !!document.fullscreenElement;
    });
    this.selectedYear = this.yearRange.find(
      (opt) => opt.value === this.currentYear,
    );
    this.selectedMonth = this.months[this.currentMonth];

    this.applyShiftSelectionFromUrl();
    if (!this.applyAttendanceReportDateQueryFromUrl()) {
      this.applyDefaultPeriodDateRange();
    }

    this.generateCalendar().then();
  },

  methods: {
    /**
     * Optional GET query on the page URL for the report range, e.g.
     * /time/displayAttendanceReportCriteria?startDate=2026-01-01&endDate=2026-01-31&shifts=1,2
     * Aliases: fromDate / toDate (YYYY-MM-DD). shifts is a comma-separated list of work shift ids.
     * Both must be present and valid; start must be <= end or values are ignored.
     */
    /**
     * Default range is the configured period, from the start day through the end day.
     */
    applyDefaultPeriodDateRange() {
      const from = this.defaultFromDate;
      const to = this.defaultToDate;
      const startM = from ? moment(from, 'YYYY-MM-DD', true) : null;
      const endM = to ? moment(to, 'YYYY-MM-DD', true) : null;
      if (startM?.isValid() && endM?.isValid() && !startM.isAfter(endM)) {
        this.filters.fromDate = startM.format('YYYY-MM-DD');
        this.filters.toDate = endM.format('YYYY-MM-DD');
        return true;
      }
      this.filters.fromDate = moment().startOf('month').format('YYYY-MM-DD');
      this.filters.toDate = moment().format('YYYY-MM-DD');
      return false;
    },
    applyShiftSelectionFromUrl() {
      this.shiftOptions = (this.workShifts || []).map((shift) => ({
        id: Number(shift.id),
        label: shift.label,
        hasEmployees: shift.hasEmployees !== false,
      }));
      const params = new URLSearchParams(window.location.search);
      if (!params.has('shifts')) {
        this.selectedShifts = this.shiftOptions.filter(
          (shift) => shift.hasEmployees,
        );
        return;
      }
      const ids = new Set(
        String(params.get('shifts'))
          .split(',')
          .map((value) => value.trim())
          .filter((value) => value !== '')
          .map((value) => Number(value))
          .filter((id) => Number.isInteger(id) && id > 0),
      );
      this.selectedShifts = this.shiftOptions.filter((shift) =>
        ids.has(shift.id),
      );
    },
    selectedShiftIds() {
      return (this.selectedShifts || [])
        .map((shift) => Number(shift.id))
        .filter((id) => Number.isInteger(id) && id > 0)
        .sort((left, right) => left - right);
    },
    employeeMatchesSelectedShift(employee) {
      const rawShiftId = employee?.employee?.workShift?.id;
      const shiftId = Number(rawShiftId);
      const hasShift =
        rawShiftId !== null &&
        rawShiftId !== undefined &&
        rawShiftId !== '' &&
        Number.isInteger(shiftId) &&
        shiftId > 0;
      if (!hasShift) {
        return false;
      }
      if (this.shiftOptions.length === 0) {
        return true;
      }
      const selectedIds = new Set(this.selectedShiftIds());
      if (selectedIds.size === 0) {
        return false;
      }
      return selectedIds.has(shiftId);
    },
    applyAttendanceReportDateQueryFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const rawStart = params.get('startDate') || params.get('fromDate');
      const rawEnd = params.get('endDate') || params.get('toDate');
      const parse = (s) => {
        if (!s) {
          return null;
        }
        const m = moment(s, 'YYYY-MM-DD', true);
        return m.isValid() ? m : null;
      };
      const startM = parse(rawStart);
      const endM = parse(rawEnd);
      if (!startM || !endM || startM.isAfter(endM)) {
        return false;
      }
      this.filters.fromDate = startM.format('YYYY-MM-DD');
      this.filters.toDate = endM.format('YYYY-MM-DD');
      return true;
    },
    /**
     * Writes current from/to filters to the URL as startDate & endDate (replaceState).
     * Keeps other query keys; drops legacy fromDate/toDate aliases when setting canonical params.
     */
    syncAttendanceReportDateToUrl() {
      const from = this.filters.fromDate;
      const to = this.filters.toDate;
      if (!from || !to) {
        return;
      }
      const startM = moment(from, 'YYYY-MM-DD', true);
      const endM = moment(to, 'YYYY-MM-DD', true);
      if (!startM.isValid() || !endM.isValid() || startM.isAfter(endM)) {
        return;
      }
      const params = new URLSearchParams(window.location.search);
      params.set('startDate', from);
      params.set('endDate', to);
      params.set('shifts', this.selectedShiftIds().join(','));
      params.delete('fromDate');
      params.delete('toDate');
      const qs = params.toString();
      const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${
        window.location.hash || ''
      }`;
      window.history.replaceState(window.history.state, '', next);
    },
    async toggleFullscreen() {
      const container = this.$refs.fullscreenContainer;

      if (!this.isFullscreen) {
        try {
          await container.requestFullscreen();
          this.isFullscreen = true;
        } catch {
          // Fullscreen API may reject (e.g. user gesture required)
        }
      } else {
        try {
          await document.exitFullscreen();
          this.isFullscreen = false;
        } catch {
          // exitFullscreen may reject if not in fullscreen
        }
      }
    },
    // filterDepartment() {
    //   const defaultSubunit = this.subunits.find(sub =>
    //       sub.label.includes('Starworks Technologies Indonesia')
    //   );
    //   this.filteredSubunits = defaultSubunit ? [defaultSubunit] : [];
    //   this.selectedSubUnit = this.filteredSubunits.length
    //       ? this.filteredSubunits[0].label
    //       : null;
    // },
    async exportToExcel() {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Attendance');

      // Styling
      const borderStyle = {
        top: {style: 'thin'},
        left: {style: 'thin'},
        bottom: {style: 'thin'},
        right: {style: 'thin'},
      };

      const centerStyle = {
        alignment: {horizontal: 'center', vertical: 'middle', wrapText: true},
        border: borderStyle,
        font: {bold: true, size: 8},
      };

      const normalCenter = {
        alignment: {horizontal: 'center', vertical: 'middle', wrapText: true},
        border: borderStyle,
        font: {size: 8},
      };

      const textLeft = {
        alignment: {horizontal: 'left', vertical: 'middle', wrapText: true},
        border: borderStyle,
        font: {size: 8},
      };

      const formStyle = {
        alignment: {horizontal: 'left', vertical: 'middle'},
        font: {size: 8},
      };

      // Data Rows
      const periodRow = worksheet.addRow([
        '',
        'Report Period',
        `${this.filters.fromDate} - ${this.filters.toDate}`,
      ]);
      const shiftRow = worksheet.addRow([
        '',
        'Work Shift',
        this.reportShiftLabel,
      ]);
      worksheet.addRow([]);
      [periodRow, shiftRow].forEach((row) => {
        row.getCell(2).style = formStyle;
        row.getCell(3).style = formStyle;
      });

      // Header
      const headerRow1 = ['No', 'Employee Name', 'Job Title'];
      const headerRow2 = ['', '', ''];

      this.calendarDates.forEach((d) => {
        headerRow1.push(d.date);
        headerRow2.push(this.getFullDayName(d.full).toUpperCase());
      });

      PAYROLL_TOTAL_COLUMNS.forEach((column) => {
        headerRow1.push(column.label);
        headerRow2.push('');
      });

      // Add headers
      worksheet.addRow(headerRow1);
      worksheet.addRow(headerRow2);

      // Merge No, Employee Name & Job Title cells vertically
      worksheet.mergeCells('A4:A5');
      worksheet.mergeCells('B4:B5');
      worksheet.mergeCells('C4:C5');

      const firstTotalCol = this.calendarDates.length + 4;
      PAYROLL_TOTAL_COLUMNS.forEach((_, index) => {
        const col = firstTotalCol + index;
        worksheet.mergeCells(4, col, 5, col);
      });

      // Apply header style
      worksheet.getRow(4).eachCell((cell) => (cell.style = centerStyle));
      worksheet.getRow(5).eachCell((cell) => (cell.style = centerStyle));

      // Set column widths (compact)
      worksheet.columns = [
        {width: 4.5},
        {width: 22},
        {width: 22},
        ...this.calendarDates.map(() => ({width: 5.5})),
        ...PAYROLL_TOTAL_COLUMNS.map(() => ({width: 8})),
      ];

      // For Departments rows
      const departments = this.employeesByDepartment || [];
      let empCounter = 1;

      for (const dept of departments) {
        const deptRow = worksheet.addRow([dept.department.toUpperCase()]);

        const lastColIndex = headerRow1.length;
        const lastColLetter =
          worksheet.getColumn(lastColIndex).letter ||
          String.fromCharCode(64 + lastColIndex);
        worksheet.mergeCellsWithoutStyle(
          `A${deptRow.number}:${lastColLetter}${deptRow.number}`,
        );

        for (let col = 1; col <= lastColIndex; col++) {
          const cell = deptRow.getCell(col);
          cell.border = {
            top: {style: 'thin'},
            bottom: {style: 'thin'},
            ...(col === 1 ? {left: {style: 'thin'}} : {}),
            ...(col === lastColIndex ? {right: {style: 'thin'}} : {}),
          };
          if (col === 1) {
            cell.font = {bold: true, size: 8};
            cell.alignment = {horizontal: 'left', vertical: 'middle'};
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: {argb: 'FFD9D9D9'},
            };
          }
        }

        deptRow.height = 14;
        for (const emp of dept.employees) {
          let nameWithShift = emp.name;
          if (emp.workShift?.startTime && emp.workShift?.endTime) {
            nameWithShift += `\n${emp.workShift.startTime} - ${emp.workShift.endTime}`;
          }
          // Prepend the numbering in the row
          const row = [
            empCounter,
            !this.showWorkShift ? emp.name : nameWithShift,
            emp.jobTitle,
          ];

          this.calendarDates.forEach((date) => {
            const att = emp.attendance[date.full];

            if (Array.isArray(att)) {
              // Get unique `text` values only
              const uniqueTexts = Array.from(
                new Set(att.map((a) => a.text).filter(Boolean)),
              );
              row.push(uniqueTexts.join(', '));
            } else if (att?.text) {
              row.push(att.text);
            } else {
              row.push('');
            }
          });

          PAYROLL_TOTAL_COLUMNS.forEach((column) => {
            row.push(
              formatPayrollTotalValue(
                column.key,
                emp.payrollTotals?.[column.key],
              ),
            );
          });

          const newRow = worksheet.addRow(row);

          // Style for name & title
          newRow.getCell(1).style = normalCenter;
          newRow.getCell(2).style = textLeft;
          newRow.getCell(3).style = textLeft;

          this.calendarDates.forEach((date, index) => {
            const colIndex = index + 4; //  from cell D (No, Name, Title)
            const att = emp.attendance[date.full];

            let attClass = '';
            if (Array.isArray(att) && att.length > 0) {
              attClass = this.getAttendanceClass(att);
            } else if (att?.text) {
              attClass = this.getAttendanceClass([{text: att.text}]);
            }

            const cell = newRow.getCell(colIndex);
            cell.alignment = normalCenter.alignment;
            cell.border = normalCenter.border;

            const customItem = Array.isArray(att)
              ? att.find((a) => normalizeAttendanceHexColor(a.color))
              : null;
            const customBg = customItem
              ? normalizeAttendanceHexColor(customItem.color)
              : null;

            let colorStyle;
            if (customBg) {
              const bgArgb = webHexToExcelArgb(customBg);
              const fgHex = resolvedAttendanceForegroundHex(customItem);
              const fgArgb = webHexToExcelArgb(fgHex);
              colorStyle = {
                bg: bgArgb,
                color: fgArgb || 'FF000000',
              };
            } else {
              colorStyle = EXPORT_ATTENDANCE_COLOR_MAP[attClass];
            }
            if (colorStyle) {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: {argb: colorStyle.bg},
              };
              cell.font = {
                size: 8,
                color: {argb: colorStyle.color},
              };
            } else {
              cell.font = {size: 8};
            }

            // Add comment on cell
            if (Array.isArray(att)) {
              const withTooltip = att.filter((a) => a.tooltip);
              const hoursList = withTooltip.flatMap(
                (a) =>
                  a.hours?.map((h) => `${Number(h).toFixed(2)} hours`) || [],
              );
              const noteTexts = [];
              if (hoursList.length > 0) {
                noteTexts.push(
                  ...hoursList.map((text) => ({
                    font: {size: 8, color: {argb: 'FF000000'}},
                    text: text + '\n',
                  })),
                );
              } else {
                const tooltipOnlyTexts = withTooltip
                  .filter((a) => !isWorkAttendanceEntry(a))
                  .map((a) => ({
                    font: {size: 8, color: {argb: 'FF000000'}},
                    text: a.tooltip + '\n',
                  }));

                if (tooltipOnlyTexts.length > 0) {
                  noteTexts.push(...tooltipOnlyTexts);
                }
              }

              if (noteTexts.length > 0) {
                cell.note = {
                  texts: noteTexts,
                  margins: {
                    inset: [0.1, 0.1, 0.1, 0.1],
                  },
                };
              }
            }
          });

          const totalStartCol = this.calendarDates.length + 4;
          const totalEndCol = totalStartCol + PAYROLL_TOTAL_COLUMNS.length - 1;
          for (let c = totalStartCol; c <= totalEndCol; c++) {
            const totalCell = newRow.getCell(c);
            totalCell.alignment = normalCenter.alignment;
            totalCell.border = normalCenter.border;
            totalCell.font = {size: 8};
          }

          empCounter++;
          newRow.height = 15;
        }
      }

      worksheet.getRow(1).height = 15;
      worksheet.getRow(2).height = 15;
      worksheet.getRow(3).height = 15;
      worksheet.getRow(4).height = 13;
      worksheet.getRow(5).height = 13;

      // Uniform compact height for remaining rows (overrides above where set)
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 5 && (row.height == null || row.height === undefined)) {
          row.height = 15;
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const fileName = `Attendance Reports-${this.filters.fromDate}-${this.filters.toDate}`;
      saveAs(new Blob([buffer]), `${fileName}.xlsx`);
    },

    selectedShiftLabel() {
      return (this.selectedShifts || [])
        .map((shift) => shift.label)
        .filter(Boolean)
        .join(', ');
    },

    async generateCalendar() {
      this.syncAttendanceReportDateToUrl();
      this.reportShiftLabel = this.selectedShiftLabel();
      this.isLoading = true;

      this.rawEmployees = await this.loadAttendanceReport();

      const startDate = new Date(this.filters.fromDate);
      const endDate = new Date(this.filters.toDate);

      const dates = [];
      const current = new Date(startDate);

      while (current <= endDate) {
        const fullDate = [
          current.getFullYear(),
          String(current.getMonth() + 1).padStart(2, '0'),
          String(current.getDate()).padStart(2, '0'),
        ].join('-');

        dates.push({
          full: fullDate,
          date: current.getDate(),
        });

        current.setDate(current.getDate() + 1);
      }

      this.calendarDates = dates;
      this.isLoading = false;

      this.transformEmployeeData(!!this.showUnassigned);
      if (this.canViewAdminActionLog) {
        await this.loadAdminActionLogs();
      } else {
        this.adminActionLogRows = [];
      }
    },

    toggleUnassigned() {
      this.transformEmployeeData(!!this.showUnassigned);
    },

    formatPayrollTotal(key, value) {
      return formatPayrollTotalValue(key, value);
    },

    async loadAttendanceReport() {
      const shiftIds = this.selectedShiftIds();
      const {data} = await http.getAll({
        startDate: this.reportDateParam(this.filters.fromDate),
        endDate: this.reportDateParam(this.filters.toDate),
        ...(shiftIds.length > 0 ? {shifts: shiftIds} : {}),
      });
      // const filtered = data.data.find(
      //   (dept) => dept.department === this.selectedSubUnit.label,
      // );

      return data.data || [];
    },

    transformEmployeeData(filterUnassigned) {
      this.employeesByDepartment = this.rawEmployees
        .filter(
          (dept) =>
            filterUnassigned || dept.department.toLowerCase() !== 'unassigned',
        )
        .map((dept) => {
          return {
            department: dept.department,
            employees: dept.employees
              .filter((emp) => this.employeeMatchesSelectedShift(emp))
              .map((emp) => {
                const attendance = {};
                const calendarByDate = {};

                emp.calendar.forEach((entry) => {
                  calendarByDate[entry.date] = entry;
                  attendance[entry.date] = payrollDayCells(entry);
                });

                const empNumber = emp.employee?.empNumber;
                const employeeCode = emp.employee?.employeeId;
                return {
                  // Prefer emp_number so keys stay unique (employeeId can be null / duplicated).
                  id:
                    empNumber != null && empNumber !== ''
                      ? empNumber
                      : employeeCode,
                  name: emp.employee.fullName,
                  jobTitle: emp.employee.jobTitle,
                  workShift: emp.employee.workShift,
                  payrollTotals: sumPayrollTotals(emp.calendar),
                  attendance,
                  calendarByDate,
                };
              }),
          };
        })
        .filter((dept) => dept.employees.length > 0);
    },

    openAttendanceCellDetail(employee, dateIso) {
      const dayEntry = employee.calendarByDate?.[dateIso];
      if (!dayEntry) {
        return;
      }
      this.attendanceDetailModal = {
        show: true,
        employeeName: employee.name,
        empNumber: employee.id,
        dayEntry,
      };
    },

    closeAttendanceCellDetail() {
      this.attendanceDetailModal.show = false;
    },

    openPeriodDetails(employee) {
      const entries = Object.values(employee.calendarByDate || {}).sort(
        (left, right) => String(left.date).localeCompare(String(right.date)),
      );
      this.periodDetails = {
        show: true,
        employeeName: employee.name,
        entries,
      };
    },

    closePeriodDetails() {
      this.periodDetails.show = false;
    },

    async onAttendanceDetailSaved() {
      this.rawEmployees = await this.loadAttendanceReport();
      this.transformEmployeeData(!!this.showUnassigned);
      if (this.canViewAdminActionLog) {
        await this.loadAdminActionLogs();
      }
    },

    formatAdminActionLogDate(isoDate) {
      if (!isoDate) {
        return '-';
      }
      const parsed = parseDate(isoDate);
      if (!parsed) {
        return isoDate;
      }
      return formatDate(parsed, this.jsDateFormat, {locale: this.locale});
    },

    parseAdminActionLogServerDateTime(value) {
      if (!value) {
        return null;
      }

      // Backend stores performed_at in UTC; render in the browser's local timezone.
      const parsed = new Date(`${value.replace(' ', 'T')}Z`);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    },

    formatAdminActionLogPerformedAt(value) {
      const parsed = this.parseAdminActionLogServerDateTime(value);
      if (!parsed) {
        return value || '-';
      }

      const datePart = formatDate(parsed, this.jsDateFormat, {
        locale: this.locale,
      });
      const timePart = formatTime(parsed, 'HH:mm:ss', {locale: this.locale});
      return `${datePart} ${timePart}`;
    },

    formatAdminActionLogPerformedBy(performedBy) {
      if (!performedBy) {
        return '-';
      }
      if (performedBy.firstName || performedBy.lastName) {
        return this.tEmpName(performedBy, {excludePastEmpTag: false});
      }

      return performedBy.userName || '-';
    },

    normalizeAdminActionLogRow(item) {
      const categoryKey =
        item.category === 'leave'
          ? 'time.admin_action_category_leave'
          : 'time.admin_action_category_attendance';
      const actionMap = {
        created: 'time.admin_action_created',
        updated: 'time.admin_action_updated',
        deleted: 'time.admin_action_deleted',
        assigned: 'time.admin_action_assigned',
        cancelled: 'time.admin_action_cancelled',
        approved: 'time.admin_action_approved',
      };
      const actionKey = actionMap[item.action] || item.action;

      let employeeLabel = '-';
      if (item.employee?.firstName || item.employee?.lastName) {
        employeeLabel = this.tEmpName(item.employee, {
          excludePastEmpTag: false,
        });
      }

      let targetDateLabel = '-';
      if (item.targetDate) {
        targetDateLabel = this.formatAdminActionLogDate(item.targetDate);
        if (item.targetToDate && item.targetToDate !== item.targetDate) {
          targetDateLabel += ` – ${this.formatAdminActionLogDate(
            item.targetToDate,
          )}`;
        }
      }

      return {
        id: item.id,
        empNumber: item.employee?.empNumber ?? null,
        categoryKey: item.category,
        performedByUserId: item.performedBy?.id ?? null,
        performedAt: this.formatAdminActionLogPerformedAt(item.performedAt),
        category: this.$t(categoryKey),
        action: this.$t(actionKey),
        employee: employeeLabel,
        targetDate: targetDateLabel,
        performedBy: this.formatAdminActionLogPerformedBy(item.performedBy),
        summary: item.summary || '-',
      };
    },

    resolveAdminActionLogFilterId(value) {
      if (value == null || value === '') {
        return null;
      }
      if (typeof value === 'object' && value.id != null && value.id !== '') {
        return value.id;
      }

      return value;
    },

    resetAdminActionLogFilters() {
      this.adminActionLogFilters.empNumber = null;
      this.adminActionLogFilters.category = null;
      this.adminActionLogFilters.performedByUserId = null;
    },

    reportDateParam(value) {
      if (!value) {
        return null;
      }
      const iso = moment(value, 'YYYY-MM-DD', true);
      if (iso.isValid()) {
        return iso.format('YYYY-MM-DD');
      }
      const userFmt = moment(value, this.userDateFormat, true);
      if (userFmt.isValid()) {
        return userFmt.format('YYYY-MM-DD');
      }
      const fallback = moment(value);
      return fallback.isValid() ? fallback.format('YYYY-MM-DD') : null;
    },

    async loadAdminActionLogs() {
      const startDate = this.reportDateParam(this.filters.fromDate);
      const endDate = this.reportDateParam(this.filters.toDate);
      if (!startDate || !endDate) {
        this.adminActionLogRows = [];
        this.resetAdminActionLogFilters();
        return;
      }
      try {
        const {data} = await adminActionLogHttp.getAll({
          startDate,
          endDate,
        });
        const rows = data?.data || [];
        this.adminActionLogRows = rows.map((row) =>
          this.normalizeAdminActionLogRow(row),
        );
        this.resetAdminActionLogFilters();
      } catch {
        this.adminActionLogRows = [];
        this.resetAdminActionLogFilters();
      }
    },

    editAttendance(empId, date) {
      const emp = this.employees.find((e) => e.id === empId);
      if (!emp.attendance) emp.attendance = {};
      this.editingCell = {empId, date};
      // nextTick(() => {
      //   const input = document.querySelector(`[data-edit-cell="${empId}-${date}"]`);
      //   if (input) input.focus();
      // });
    },

    // isEditing(empId, date) {
    //   return this.editingCell.value.empId === empId && this.editingCell.value.date === date;
    // },

    // stopEditing() {
    //   this.editingCell.value = {empId: null, date: null};
    // },

    getFullDayName(iso) {
      return new Date(iso).toLocaleDateString('en-US', {weekday: 'short'});
    },

    getAttendanceClass(attendanceArray) {
      if (!Array.isArray(attendanceArray) || attendanceArray.length === 0) {
        return '';
      }

      if (attendanceArray.some((entry) => entry.isOff)) {
        return 'off-day';
      }

      if (attendanceArray.some(isWorkAttendanceEntry)) {
        return 'normal-attendance';
      }

      const combinedText = attendanceArray
        .map((a) => a.leaveMark || a.text)
        .join(' ')
        .toUpperCase();
      if (combinedText.includes('OFF')) {
        return 'off-day';
      }
      if (
        attendanceArray.some((entry) => entry.isAbsence) ||
        combinedText.split(/[\s,]+/).includes('ABS')
      ) {
        return 'absence-day';
      }
      if (
        combinedText.includes('PH') ||
        combinedText.includes('PD') ||
        combinedText.includes('IID')
      ) {
        return 'leave-holiday';
      }

      if (attendanceArray.some((a) => normalizeAttendanceHexColor(a.color))) {
        return '';
      }

      const tokens = combinedText.split(/[\s,]+/).filter(Boolean);
      if (tokens.some((t) => t === 'SL') || combinedText.includes('SICK')) {
        return 'leave-sl';
      }

      for (const token of tokens) {
        if (token === 'OFF') {
          continue;
        }
        if (/^[A-Z0-9-]{1,10}$/i.test(token)) {
          return 'leave-al';
        }
      }

      return 'normal-attendance';
    },

    getAttendanceCellStyle(attendanceArray) {
      if (!Array.isArray(attendanceArray)) {
        return {};
      }
      const item = attendanceArray.find((a) =>
        normalizeAttendanceHexColor(a.color),
      );
      if (!item) {
        return {};
      }
      const bg = normalizeAttendanceHexColor(item.color);
      const fg = resolvedAttendanceForegroundHex(item);
      return {
        backgroundColor: bg,
        color: fg,
      };
    },

    formatDate(iso) {
      const d = new Date(iso);
      const dd = String(d.getDate()).padStart(2, '0');
      // const mm = String(d.getMonth() + 1).padStart(2, '0');
      // const yy = String(d.getFullYear()).slice(2);
      return `${dd}`;
    },
  },
};
</script>

<style scoped>
.attendance-report-page {
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.attendance-report-page .orangehrm-paper-container {
  min-width: 0;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
}

.attendance-admin-action-log {
  margin-top: 1rem;
}

.attendance-report-results {
  overflow: hidden;
  margin-top: 26px;
}

.attendance-report-export {
  display: flex;
  justify-content: flex-end;
  padding: 0.75rem 1rem 1rem;
  background: #e8eaef;
}

.attendance-report-fullbleed {
  min-width: 0;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
}

.attendance-report-scroll-host {
  min-width: 0;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
}

.loader-wrapper {
  height: 30vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.spacer {
  padding: 1em 0;
}

.calendar-wrapper {
  font-family: Arial, sans-serif;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding-bottom: 0.5em;
}

.calendar-container {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  border: 1px solid #6f7d90;
  border-radius: 5px;
  overflow-x: auto;
  position: relative;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #9ca3af transparent;
}

.calendar-inner {
  display: flex;
  min-width: max-content;
  width: max-content;
}

.calendar-container::-webkit-scrollbar {
  height: 8px;
}

.calendar-container::-webkit-scrollbar-track {
  background: transparent;
}

.calendar-container::-webkit-scrollbar-thumb {
  background-color: #9ca3af;
  border-radius: 4px;
}

.calendar-container::-webkit-scrollbar-thumb:hover {
  background-color: #6b7280;
}

.controls {
  margin-bottom: 10px;
}

.controls select {
  margin-right: 10px;
  padding: 5px;
}

.cell {
  display: flex;
  justify-content: center;
  width: 44px;
  align-items: center;
  min-width: 44px;
  max-width: 120px;
  box-sizing: border-box;
}

.header-cell {
  box-sizing: border-box;
  color: #64728c;
  font-weight: bold;
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  height: 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  white-space: normal;
  text-align: center;
  line-height: 1.2;
  padding: 0 4px;
  border-bottom: 1px solid #6f7d90;
  border-right: 1px solid #6f7d90;
}

.header-cell.date {
  z-index: 5;
  background: #f8f9fa;
}

.header-cell.day {
  text-transform: uppercase;
  z-index: 4;
  background: #f8f9fa;
}

.attendance-cell {
  box-sizing: border-box;
  font-size: 11px;
  color: #64728c;
  font-family: Nunito Sans, sans-serif;
  height: 26px;
  min-height: 26px;
  max-height: 26px;
  text-align: center;
  border-bottom: 1px solid #6f7d90;
  border-right: 1px solid #6f7d90;
  background-color: #fff;
  cursor: pointer;
}

.edit-input {
  width: 100%;
  height: 100%;
  border: none;
  text-align: center;
  font-size: 11px;
  color: #64728c;
  font-family: Nunito Sans, sans-serif;
  outline: none;
  box-sizing: border-box;
}

.sidebar {
  position: sticky;
  left: 0;
  z-index: 25;
  width: 250px;
  min-width: 250px;
  flex-shrink: 0;
  align-self: flex-start;
  background-color: #f8f9fa;
  border-right: 1px solid #6f7d90;
  box-shadow: 4px 0 10px -6px rgba(0, 0, 0, 0.18);
}

.sidebar-payroll-totals {
  width: 416px;
  min-width: 416px;
  flex-shrink: 0;
  background-color: #f8f9fa;
  position: static;
  z-index: 1;
}

.payroll-total-header,
.payroll-total-values {
  flex-direction: row;
  align-items: stretch;
  justify-content: flex-start;
  padding: 0;
  gap: 0;
}

.payroll-total-col {
  box-sizing: border-box;
  flex: 0 0 52px;
  width: 52px;
  min-width: 52px;
  max-width: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-right: 1px solid #6f7d90;
  border-bottom: 1px solid #6f7d90;
  background: #fff;
  color: #64728c;
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  line-height: 1.1;
}

.employee-cell {
  box-sizing: border-box;
  color: #64728c;
  font-family: Nunito Sans, sans-serif;
  height: 26px;
  min-height: 26px;
  max-height: 26px;
  line-height: 1.15;
  padding: 1px 6px;
  border-bottom: 1px solid #6f7d90;
  background: #fff;
  text-align: left;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-direction: column;
  gap: 0;
  overflow: hidden;
}

.employee-name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
  min-width: 0;
}

.employee-cell .employee-name {
  line-height: 13px;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  flex: 1;
  min-width: 0;
}

.employee-details {
  flex: 0 0 auto;
  border: 0;
  background: none;
  padding: 0;
  margin: 0;
  color: #2563eb;
  font-family: Nunito Sans, sans-serif;
  font-size: 10px;
  line-height: 13px;
  cursor: pointer;
  white-space: nowrap;
}

.employee-details:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.employee-cell .employee-job {
  line-height: 11px;
  font-size: 9px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.calendar-inner--with-shift .employee-cell,
.calendar-inner--with-shift .attendance-cell {
  height: 40px;
  min-height: 40px;
  max-height: 40px;
}

.calendar-inner--with-shift .employee-name-row .employee-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.calendar-inner--with-shift .employee-cell .employee-job {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
}

.header-cell.empty {
  box-sizing: border-box;
  height: 60px;
  border: none;
  border-bottom: 1px solid #6f7d90;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;

  position: sticky;
  top: 0;
  z-index: 10;
}

.calendar-scroll {
  position: relative;
  z-index: 0;
  min-width: 0;
}

.calendar-row {
  display: flex;
}

.calendar-row.attendance-row {
  position: sticky;
  top: 30px;
  z-index: 10;
}

.calendar-row.date-row {
  position: sticky;
  top: 0;
  z-index: 10;
}

.off-day {
  background-color: #deecff;
  color: #1e3a5f;
}

.absence-day {
  background-color: #ffe5e5;
  color: #9f1239;
}

.leave-holiday {
  background-color: #ddf0e2;
  color: #14532d;
}

.leave-sl {
  background-color: #fde6e6;
  color: #991b1b;
}

.leave-al {
  background-color: #dbf1d9;
  color: #14532d;
}

.attendance-content {
  padding: 0 1px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 0;
}

.department-row {
  box-sizing: border-box;
  font-size: 10px;
  color: #64728c;
  font-weight: bold;
  background: #f0f0f0;
  border-bottom: 1px solid #6f7d90;
  text-transform: uppercase;
  height: 20px;
  min-height: 20px;
  max-height: 20px;
  display: flex;
  align-items: center;
  padding-left: 6px;
  overflow: hidden;
}

.department-label {
  box-sizing: border-box;
  background: #f3f4f6;
  border-bottom: 1px solid #6f7d90;
  height: 20px;
  min-height: 20px;
  max-height: 20px;
  overflow: hidden;
}

.department-cell {
  box-sizing: border-box;
  grid-column: span 99;
  padding: 2px 6px;
}

.fullscreen-container {
  background: white;
  transition: opacity 0.3s ease;
}

.fullscreen-container.active {
  display: block;
  background: white;
  width: 100vw;
  height: 100vh;
  position: fixed;
  inset: 0;
  z-index: 999;
  opacity: 1;
}

.oxd-icon {
  position: absolute;
  top: 4px;
  left: 4px;
  cursor: pointer;
  font-size: 16px;
}

.attendance-admin-action-log-filters {
  padding: 0 1.5rem 1rem;
}

.header-cell.empty.payroll-total-header {
  align-items: stretch;
  justify-content: flex-start;
  flex-wrap: nowrap;
  padding: 0;
  gap: 0;
  border: none;
  height: 60px;
  min-height: 60px;
  max-height: 60px;
}

.payroll-total-header .payroll-total-col {
  height: 60px;
  background: #f8f9fa;
  font-weight: 700;
  font-size: 10px;
  text-transform: uppercase;
}

.employee-cell.payroll-total-values {
  flex-direction: row;
  align-items: stretch;
  justify-content: flex-start;
  padding: 0;
  gap: 0;
  border: none;
  background: transparent;
  height: 26px;
  min-height: 26px;
  max-height: 26px;
}

.payroll-total-values .payroll-total-col {
  height: 26px;
}

.calendar-inner--with-shift .employee-cell.payroll-total-values,
.calendar-inner--with-shift .payroll-total-values .payroll-total-col {
  height: 40px;
  min-height: 40px;
  max-height: 40px;
}

.sidebar-payroll-totals .department-row {
  box-sizing: border-box;
  width: 416px;
  padding-left: 0;
  border-right: 1px solid #6f7d90;
  border-bottom: 1px solid #6f7d90;
}
</style>
